import BigNumber from 'bignumber.js';

import { AsyncResp, BaseReq, requestApi } from '#/api/fetch-api';

import { StarknetSignature } from '#/features/wallets/paraclear/wallet';

import { msToDate } from '#/utils/date';
import getQueryString from '#/utils/getQueryString';

import { UnixTimeMs } from './types';

interface PostVaultReq extends BaseReq {
  readonly parentAccount: `0x${string}`;
  readonly vaultOperatorAccount: string;
  readonly vaultOperatorAccountDerivationPath: string;
  readonly vaultOperatorPublicKey: string;
  readonly vaultOperatorSignature: StarknetSignature;
  readonly lockupPeriod: number;
  readonly profitShare: number;
  readonly maxTvl: number | null;
  readonly name: string;
  readonly description: string;
}

export type PostVaultResp = null;

export async function postVaults(req: PostVaultReq): AsyncResp<PostVaultResp> {
  const {
    signal,
    parentAccount,
    vaultOperatorAccount,
    vaultOperatorAccountDerivationPath,
    vaultOperatorSignature,
    lockupPeriod,
    profitShare,
    maxTvl,
    name,
    description,
    vaultOperatorPublicKey,
  } = req;

  const resp = await requestApi<PostVaultResp>({
    signal,
    method: 'POST',
    url: `/vaults`,
    headers: {
      'PARADEX-PARENT-ACCOUNT': `prdx:${parentAccount}`,
      'PARADEX-STARKNET-ACCOUNT': vaultOperatorAccount,
      'PARADEX-STARKNET-ACCOUNT-DERIVATION-PATH':
        vaultOperatorAccountDerivationPath,
      'PARADEX-STARKNET-SIGNATURE': JSON.stringify([
        vaultOperatorSignature.r.toString(),
        vaultOperatorSignature.s.toString(),
      ]),
    },
    body: {
      deposit_tx_signature: '0x1_not_implemented', // WIP: not implemented on the Cloud
      lockup_period: lockupPeriod,
      profit_share: profitShare,
      max_tvl: maxTvl,
      name,
      public_key: vaultOperatorPublicKey,
      description,
    },
  });

  return resp;
}

export type VaultStatus = 'INITIALIZING' | 'ACTIVE' | 'CLOSED' | 'FAILED';

export type VaultKind = 'protocol' | 'user' | 'vtf';

export interface RawVault {
  readonly kind: VaultKind;
  readonly address: string;
  readonly description: string;
  readonly operator_account: string;
  readonly owner_account: string;
  readonly token_address: string;
  readonly name: string;
  readonly created_at: UnixTimeMs;
  readonly last_updated_at: UnixTimeMs;
  readonly status: VaultStatus;
  readonly profit_share: number;
  readonly lockup_period: number;
  readonly max_tvl: number;
  /**
   * According to discussions, this may not be present when
   * fetching list of vaults, only when fetching a single vault.
   */
  readonly strategies?: readonly RawVaultStrategy[];
}

export interface RawVaultStrategy {
  readonly address: string;
  readonly name: string;
}

export interface Vault
  extends Omit<
    RawVault,
    | 'created_at'
    | 'last_updated_at'
    | 'profit_share'
    | 'lockup_period'
    | 'max_tvl'
    | 'strategies'
  > {
  readonly created_at: Date;
  readonly last_updated_at: Date;
  readonly profit_share: BigNumber;
  readonly lockup_period: BigNumber;
  readonly max_tvl: BigNumber;
  readonly strategies?: readonly VaultStrategy[];
}

export interface VaultStrategy extends RawVaultStrategy {}

interface VaultsReq extends BaseReq {}

export interface RawVaultsResp {
  readonly results: readonly RawVault[];
}

export interface VaultsResp {
  readonly results: readonly Vault[];
}

export async function getVaults(req: VaultsReq): AsyncResp<VaultsResp> {
  const { signal } = req;

  const resp = await requestApi<RawVaultsResp>({
    signal,
    method: 'GET',
    url: `/vaults`,
  });

  if (!resp.ok) return resp;

  return {
    ...resp,
    data: {
      ...resp.data,
      results: resp.data.results.map(processVault),
    },
  };
}

export interface VaultReq extends BaseReq {
  readonly address: string;
}

export interface RawVaultResp {
  readonly results: readonly [RawVault] | [];
}

export type VaultResp = Vault | null;

export async function getVault(req: VaultReq): AsyncResp<VaultResp> {
  const { signal } = req;

  const queryString = getQueryString([['address', req.address]]);

  const resp = await requestApi<RawVaultResp>({
    signal,
    method: 'GET',
    url: `/vaults${queryString}`,
  });

  if (!resp.ok) return resp;

  const vault = resp.data.results.find((v) => v.address === req.address);

  if (vault == null) {
    return { ...resp, data: null };
  }

  return {
    ...resp,
    data: processVault(vault),
  };
}

export function processVault(vault: RawVault): Vault {
  const isVtfVault =
    typeof "0x12dbedfd7bec6d793c3e74b618cbfa3a50d96dfff39bf8ac1dc5dbe82aa5120" === 'string' &&
    vault.address !== '' &&
    "0x12dbedfd7bec6d793c3e74b618cbfa3a50d96dfff39bf8ac1dc5dbe82aa5120".includes(vault.address);
  return {
    ...vault,
    created_at: msToDate(vault.created_at),
    last_updated_at: msToDate(vault.last_updated_at),
    profit_share: new BigNumber(vault.profit_share),
    lockup_period: new BigNumber(vault.lockup_period),
    max_tvl: new BigNumber(vault.max_tvl),
    kind: isVtfVault ? 'vtf' : vault.kind,
  };
}
